import type { Competitor } from '../types'

// Sources (checked 2026-10-01): carrd.com/pro, carrd.com/docs (building, pro,
// sites, forms), carrd.com/docs/pro/trial. Carrd moved from carrd.co to carrd.com.
const carrd: Competitor = {
  slug: 'carrd',
  name: 'Carrd',
  tagline: 'One-page website builder that many people use as a link in bio.',
  site: 'https://carrd.com',
  pricingUrl: 'https://carrd.com/pro',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Carrd alternative: mychar.gg vs Carrd for your link in bio (2026)',
    description: 'mychar.gg vs Carrd for your link in bio: built-in analytics, a music player, animated effects, and one-time pricing compared with Carrd\'s yearly plans.'
  },
  hero: {
    title: 'The Carrd alternative made for your link in bio',
    description: 'Carrd builds any one-page site. mychar.gg starts with a link-in-bio page that already works, then lets you design every detail of it, live, with no code.'
  },
  summary: 'mychar.gg is the better Carrd alternative when the page is your link in bio: a music player, video clips, animated effects, YouTube and Twitch sections, and built-in analytics come ready to use, free to start or €6.99 once for Pro. Carrd is the better pick for a general one-page website with forms, payments, or custom code, starting at $19 a year with a custom domain.',
  chooseMychar: [
    'want a link in bio that looks designed without touching code',
    'want a music player, video clips, and animated effects built in',
    'want views and clicks tracked for you, with nothing to set up',
    'prefer paying once over renewing every year'
  ],
  chooseThem: [
    'need a general one-page website, not only a bio link',
    'want forms, payments, or your own HTML and CSS',
    'want a custom domain included for $19 a year'
  ],
  features: {
    free_plan: { yes: true, text: '3 sites with Carrd branding' },
    paid_plans: 'Pro Lite $9, Standard $19, Plus $49',
    billing: 'Every year',
    first_year: '$19 (Pro Standard)',
    custom_domain: { yes: true, text: 'Pro Standard and up' },
    remove_branding: { yes: true, text: 'Pro Lite and up' },
    editor: 'On the page, element by element',
    drag_drop: { yes: true, text: 'Reorder elements' },
    themes: { yes: true, text: 'Dozens of templates' },
    fonts: 'Google Fonts and web-safe fonts',
    custom_fonts: { yes: false, text: 'Not built in' },
    effects: 'Element animations and slideshows',
    cursors: { yes: false, text: 'Custom code only' },
    video_background: { yes: true, text: 'Pro Lite and up' },
    custom_code: { yes: true, text: 'Pro Standard and up' },
    links: { yes: true, text: 'Any URL, 50 elements per site' },
    music_player: { yes: false, text: 'Embeds from other services only' },
    video_clips: { yes: true, text: 'Pro Lite and up' },
    comments: false,
    view_counter: false,
    qr_code: { yes: true, text: 'Pro Lite and up' },
    youtube: 'Single video embeds',
    twitch: 'Channel and clip embeds',
    discord: { yes: false, text: 'Custom embed code only' },
    music_embeds: { yes: true, text: 'SoundCloud, Bandcamp, Mixcloud' },
    analytics_free: { yes: false, text: 'None built in' },
    analytics_live: { yes: false, text: 'Third-party tools on Pro Standard' },
    store: 'Payment forms and widgets on Pro',
    email_capture: { yes: true, text: 'Forms on Pro Standard and up' },
    mobile_app: false
  },
  plans: [
    { name: 'Basic', price: '$0', period: 'forever', note: '3 sites on a carrd.co address, with Carrd branding.' },
    { name: 'Pro Lite', price: '$9', period: '/year', note: '3 sites, no branding, video uploads, and QR codes.' },
    { name: 'Pro Standard', price: '$19', period: '/year', note: 'Adds custom domains, forms, embeds, and third-party analytics. 10 sites.' },
    { name: 'Pro Plus', price: '$49', period: '/year', note: 'Adds payment forms, automation tools, and password protection. 25 sites.' }
  ],
  plansNote: 'USD. Carrd bills yearly, and plans with more sites cost more.',
  advantages: [
    {
      title: 'Analytics built in',
      description: 'Views, clicks, and top links on every plan, plus live visitors and countries on Pro. Carrd only connects third-party analytics tools, from Pro Standard up.',
      icon: 'i-lucide-chart-line'
    },
    {
      title: 'A real music player',
      description: 'Upload your own tracks and they play while people look around. Carrd embeds players from other services but has no background music.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Animated background effects',
      description: 'Rain, snow, fire, sakura, plasma, and more, picked from a list. On Carrd that takes custom code.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'Custom cursors and trails',
      description: 'Upload a cursor or add a trail like sparkles or neon on Pro, with no scripts.',
      icon: 'i-lucide-mouse-pointer-2'
    },
    {
      title: 'Sections that update themselves',
      description: 'Your latest YouTube uploads and your Twitch live status and schedule refresh on their own.',
      icon: 'i-lucide-radio'
    },
    {
      title: 'Extras for creators, streamers, and gamers',
      description: 'Comments, friends, achievements, a game library, and a setup section are ready to add, not built by hand.',
      icon: 'i-lucide-users'
    }
  ],
  strengths: [
    {
      title: 'A full website builder',
      description: 'Carrd builds any kind of one-page site, from portfolios to landing pages, with columns, sections, and up to 1,000 sites on one account.'
    },
    {
      title: 'Forms and payments',
      description: 'Signup and contact forms connect to 20 email providers on Pro Standard, and Pro Plus adds Stripe payment forms.'
    },
    {
      title: 'Your own code',
      description: 'Pro Standard and up let you embed HTML, CSS, and JavaScript, and Pro Plus lets you download your site.'
    },
    {
      title: 'A cheaper custom domain',
      description: 'A custom domain comes with Pro Standard at $19 a year. On mychar.gg, the domain add-on is €2.99 per month.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Carrd alternative',
      paragraphs: [
        'Carrd is a website builder first. People use it as a link in bio because it is flexible and cheap, but they end up building the parts a bio page needs by hand: a stack of buttons, an embedded player from another service, an analytics script from a third party. Each of those is a separate element to place and maintain, and the free plan caps a site at 50 elements.',
        'The other common reason is analytics. Carrd has none built in. You can connect Google Analytics, Plausible, or a similar tool from Pro Standard up, but out of the box you cannot see which link people clicked.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg starts from a working link-in-bio page and lets you change everything about it. The sections a creator needs already exist: links with icons for about 35 platforms, a background music player, video clips, YouTube and Twitch sections that keep themselves current, and a Discord profile. You design the look in the [editor](/docs/customization), which works on the page itself like Carrd, except that you are styling a page that already works instead of assembling one.',
        'Analytics start with the first visit. Every plan tracks views, clicks, and your top links, and Pro adds live visitors and countries. On price, Carrd\'s yearly plans are cheap, but mychar.gg costs less over time: Pro is €6.99 once, with nothing to renew. If you want your own domain, the €2.99 per month add-on works on any plan, including Free.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup) and pick the name for mychar.gg/yourname.' },
    { title: 'Add your links', description: 'Copy the buttons from your Carrd site. Links to platforms like YouTube, TikTok, and Instagram are unlimited on Free; links to any other URL start on Pro.' },
    { title: 'Recreate the look', description: 'Pick a layout, fonts, colors, and a background, or start from a community [skin](/skins). Your Carrd background image works here too.' },
    { title: 'Move your media in', description: 'Upload tracks to the music player and clips to the page instead of embedding them from other services. The [clips guide](/docs/profile/clips) covers formats.' },
    { title: 'Swap the link', description: 'Replace your Carrd URL in every bio. If you used your own domain on Carrd, point it at mychar.gg with the [custom domain add-on](/docs/profile/custom-domain).' }
  ],
  faq: [
    {
      label: 'Is mychar.gg free like Carrd?',
      content: 'Yes. The Free plan is free forever, with unlimited platform links, a music player, and analytics. Carrd\'s free Basic plan gives you 3 sites with Carrd branding.'
    },
    {
      label: 'Is mychar.gg cheaper than Carrd?',
      content: 'Over time, yes. Carrd Pro plans renew every year at $9, $19, or $49. mychar.gg Pro is €6.99 once and Elite €12.99 once. Your own domain costs €2.99 per month on mychar.gg and is included in Carrd Pro Standard.'
    },
    {
      label: 'Can I add custom HTML or CSS like on Carrd?',
      content: 'No. mychar.gg does not run custom code. You get layouts, fonts, colors, animated effects, cursors, and card styles instead, all set visually in the editor.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes, with the custom domain add-on for €2.99 per month on any plan. Your mychar.gg/yourname link keeps working next to it. See the [custom domain guide](/docs/profile/custom-domain).'
    },
    {
      label: 'Does mychar.gg have analytics?',
      content: 'Yes, built in on every plan with no script to install: views, clicks, and top links, plus live visitors and countries on Pro and Elite. See [statistics](/docs/statistics).'
    }
  ]
}

export default carrd
